import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "../data/landing";

const EASE = [0.22, 1, 0.36, 1];

function useCountUp(target, active, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

function StatItem({ stat, index, active }) {
  const raw = useCountUp(stat.value, active);
  const display = stat.value % 1 === 0 ? Math.round(raw) : raw.toFixed(1);
  return (
    <motion.div
      className="relative text-center px-4"
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.1, ease: EASE }}
      data-testid={`stat-item-${index}`}
    >
      <div
        className="font-arial-black text-white leading-none tracking-tight"
        style={{ fontSize: "clamp(40px, 5vw, 64px)" }}
      >
        <span data-testid={`stat-value-${index}`}>{display}</span>
        <span className="text-[#5b80ff]">{stat.suffix}</span>
      </div>
      <p className="mt-3 text-[11px] sm:text-xs uppercase tracking-[0.28em] text-white/60 font-semibold">
        {stat.label}
      </p>
    </motion.div>
  );
}

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section
      ref={ref}
      className="relative bg-[#050B26] py-16 sm:py-20 border-y border-white/5 overflow-hidden"
      data-testid="stats-section"
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 120% at 50% 0%, rgba(20,64,225,0.18), transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 divide-x-0 lg:divide-x lg:divide-white/10">
          {STATS.map((stat, i) => (
            <StatItem key={stat.label} stat={stat} index={i} active={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
